import { useState } from 'react';
import { Alert, Modal, Segmented, Skeleton, Space, Typography } from 'antd';
import { DesktopOutlined, MobileOutlined } from '@ant-design/icons';
import { EmailPreview } from '../../components/email-designer/EmailPreview';
import { useSentCopy } from '../../features/campaigns/api';
import { errorMessage } from '../../api/client';
import { fmtDateTime } from '../../utils/format';

/**
 * A sent campaign email exactly as it went out: one recipient's own copy, or the first copy sent. Links do nothing
 * here, so the preview can never click someone's unsubscribe link or count as a click.
 */
export function SentEmailModal({ campaignId, recipient, open, onClose }: { campaignId: string; recipient?: { id: string; email: string } | null; open: boolean; onClose: () => void }) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const copy = useSentCopy(campaignId, recipient?.id, open);
  const c = copy.data;

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={760} title={recipient ? `Email sent to ${recipient.email}` : 'Sent email'} destroyOnHidden>
      {copy.isLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : copy.isError ? (
        <Alert type="error" showIcon title={`Could not load the email: ${errorMessage(copy.error)}`} />
      ) : !c ? (
        <Alert type="info" showIcon title={recipient ? 'This person was not sent the email.' : 'Nothing has been sent in this campaign yet.'} />
      ) : (
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <Typography.Text strong style={{ fontSize: 16, display: 'block' }}>{c.subject || '(no subject)'}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {recipient ? 'Their copy' : `As sent to ${c.to ?? 'a recipient'}`}, {fmtDateTime(c.sentAt)}. {recipient ? '' : 'Each person got their own details filled in.'}
              </Typography.Text>
            </div>
            <Segmented
              value={device}
              onChange={(v) => setDevice(v as 'desktop' | 'mobile')}
              options={[
                { value: 'desktop', label: 'Desktop', icon: <DesktopOutlined /> },
                { value: 'mobile', label: 'Mobile', icon: <MobileOutlined /> },
              ]}
            />
          </div>
          <EmailPreview html={c.html} width={device === 'desktop' ? 600 : 375} />
        </Space>
      )}
    </Modal>
  );
}
